import type { CDMOApplication } from '../lib/supabase';
import { type CDMOFacility, OPERATING_REGIONS_OPTIONS } from '../constants';
import { canonicalizeDetailedCapabilityLabels } from '../constants/detailedCapabilities';

/**
 * Shaping logic for the technical-detail accordions on the sponsor result card (scope §6).
 *
 * Kept out of the component so the rules can be unit-tested directly. Two of them are easy to get
 * subtly wrong and are the reason this is a module rather than inline JSX:
 *
 *   - the collapsed capability count is **unique across the profile**, so a capability offered at
 *     three facilities still counts once;
 *   - a facility with no location is labelled by region and its position *within that region*
 *     ("Europe — Facility 2"), never by its index in the flat array.
 *
 * Everything here reads stored CDMO data only. Nothing is inferred: a drug-product capability never
 * implies the matching delivery-device capability, because the two are separate stored values and
 * this code only ever groups what it is given (§6.4).
 */

/** A facility paired with the heading the card shows for it. */
export interface LabelledFacility {
  facility: CDMOFacility;
  /** The typed location, or "Region — Facility N" when none was entered. */
  label: string;
}

/** Capabilities under one modality heading. */
export interface ModalityGroup {
  modality: string;
  capabilities: string[];
}

/** One block in the Primary Capabilities accordion. */
export interface CapabilitySection {
  /** Facility heading, or `null` when the CDMO has no facility-level mappings at all. */
  facilityLabel: string | null;
  /** True for the trailing {@link CDMO_WIDE_LABEL} block. */
  isCdmoWide: boolean;
  groups: ModalityGroup[];
}

/** Equipment recorded at one facility. */
export interface EquipmentGroup {
  facilityLabel: string;
  items: string[];
}

/** Heading for profile capabilities not assigned to any facility. */
export const CDMO_WIDE_LABEL = 'Additional Verified Capabilities';

/** Explains what the {@link CDMO_WIDE_LABEL} block does and does not assert about a site. */
export const CDMO_WIDE_CAPTION = 'Verified at the company level; specific facility not confirmed.';

/**
 * Modality → capabilities, skipping empty modalities and non-array values from older rows.
 *
 * Resolves each label through the shared {@link canonicalizeDetailedCapabilityLabels} so a
 * profile that predates a capability-label rename (and has not gone through `CDMOProfile.tsx`'s
 * edit-and-resave path) still shows its current canonical name on the sponsor-facing card, not
 * the retired one.
 */
function modalityEntries(detailed: Record<string, string[]> | undefined): ModalityGroup[] {
  return Object.entries(detailed ?? {})
    .map(([modality, capabilities]) => ({
      modality,
      capabilities: canonicalizeDetailedCapabilityLabels(
        Array.isArray(capabilities) ? capabilities.filter(Boolean) : []
      ),
    }))
    .filter((entry) => entry.capabilities.length > 0);
}

/**
 * Facility records grouped by region, in {@link OPERATING_REGIONS_OPTIONS} order, each with its
 * display label.
 *
 * The per-region index is what gives an unnamed facility its number, so it is assigned here rather
 * than taken from the flat array position. Records whose region the CDMO no longer lists are
 * dropped — the intake stops saving those, but rows written earlier can still hold them.
 *
 * @param cdmo - CDMO profile
 * @returns Labelled facilities in display order
 */
export function groupFacilitiesByRegion(cdmo: CDMOApplication): LabelledFacility[] {
  const facilities = Array.isArray(cdmo.facilities) ? cdmo.facilities : [];
  if (facilities.length === 0) return [];

  const regions = OPERATING_REGIONS_OPTIONS.filter((region) =>
    (cdmo.operating_regions ?? []).includes(region)
  );

  return regions.flatMap((region) =>
    facilities
      .filter((facility) => facility?.region === region)
      .map((facility, index) => ({
        facility,
        label: facility.location?.trim() || `${region} — Facility ${index + 1}`,
      }))
  );
}

/**
 * Every distinct capability on the profile, counted once.
 *
 * This is the number the collapsed accordion header shows. It deliberately ignores facility
 * assignments: a capability offered at three sites is one capability, not three.
 *
 * @param cdmo - CDMO profile
 * @returns Count of unique profile-level capabilities
 */
export function countUniqueCapabilities(cdmo: CDMOApplication): number {
  const all = modalityEntries(cdmo.detailed_capabilities).flatMap((entry) => entry.capabilities);
  return new Set(all).size;
}

/**
 * Sections for the Primary Capabilities accordion.
 *
 * With facility-level mappings, capabilities are grouped by facility first and modality second, and
 * anything the CDMO claims but has not pinned to a site follows under
 * {@link CDMO_WIDE_LABEL}. Without any mappings, the original modality-only presentation is
 * returned unchanged.
 *
 * A capability is only shown under a facility if the profile still claims it, so removing it from
 * the profile removes it everywhere — the card never displays a capability the CDMO no longer
 * reports.
 *
 * @param cdmo - CDMO profile
 * @returns Ordered sections, or an empty array when the profile lists no capabilities
 */
export function buildCapabilitySections(cdmo: CDMOApplication): CapabilitySection[] {
  const byModality = modalityEntries(cdmo.detailed_capabilities);
  if (byModality.length === 0) return [];

  const labelledFacilities = groupFacilitiesByRegion(cdmo);
  /** Modality lookup for a capability, so facility groups can be ordered the same way. */
  const modalityOf = new Map<string, string>();
  byModality.forEach((entry) =>
    entry.capabilities.forEach((capability) => {
      if (!modalityOf.has(capability)) modalityOf.set(capability, entry.modality);
    })
  );

  const assigned = new Set<string>();
  const facilitySections: CapabilitySection[] = [];

  for (const { facility, label } of labelledFacilities) {
    // Facility capabilities are resolved the same way as `byModality` above, so a pre-rename
    // facility-level label still string-matches its (now-canonical) profile-level counterpart.
    const claimed = canonicalizeDetailedCapabilityLabels(facility.capabilities).filter((capability) =>
      modalityOf.has(capability)
    );
    if (claimed.length === 0) continue;

    claimed.forEach((capability) => assigned.add(capability));

    // Keep modality order consistent with the profile rather than the assignment order.
    const groups = byModality
      .map((entry) => ({
        modality: entry.modality,
        capabilities: entry.capabilities.filter((capability) => claimed.includes(capability)),
      }))
      .filter((entry) => entry.capabilities.length > 0);

    facilitySections.push({ facilityLabel: label, isCdmoWide: false, groups });
  }

  // No facility mappings at all → the original modality-only presentation.
  if (facilitySections.length === 0) {
    return [{ facilityLabel: null, isCdmoWide: false, groups: byModality }];
  }

  const leftoverGroups = byModality
    .map((entry) => ({
      modality: entry.modality,
      capabilities: entry.capabilities.filter((capability) => !assigned.has(capability)),
    }))
    .filter((entry) => entry.capabilities.length > 0);

  if (leftoverGroups.length > 0) {
    facilitySections.push({
      facilityLabel: CDMO_WIDE_LABEL,
      isCdmoWide: true,
      groups: leftoverGroups,
    });
  }

  return facilitySections;
}

/**
 * A comma acting as a thousands separator: a digit, a comma, then exactly three digits that are
 * not themselves followed by another digit. Matching the leading digit rather than using a
 * lookbehind keeps this parseable in Safari below 16.4, where lookbehind is a syntax error that
 * would take the whole bundle down.
 */
const THOUSANDS_SEPARATOR = /(\d),(?=\d{3}(?:\D|$))/g;
/**
 * Placeholder standing in for a protected comma while the string is split.
 *
 * Written as an escape rather than a literal control character: a raw NUL byte in the source
 * makes Git classify this file as binary, which costs the file its text diffs and its
 * three-way merges.
 */
const PROTECTED_COMMA = '\u0000';

/**
 * Splits the free-text equipment field into individual items.
 *
 * The intake accepts comma- or line-separated entries (§5), so both are honoured here — but a
 * comma between digits is a thousands separator, not a delimiter. Those commas are protected
 * before the split and restored after.
 *
 * @param equipment - Raw free text from the facility record
 * @returns Trimmed, non-empty items
 */
export function parseEquipmentItems(equipment: string | undefined): string[] {
  return (equipment ?? '')
    // Strip any NUL already in the text so it can't collide with the placeholder below.
    .split(PROTECTED_COMMA).join('')
    .replace(THOUSANDS_SEPARATOR, `$1${PROTECTED_COMMA}`)
    .split(/[\n,]+/)
    .map((item) => item.split(PROTECTED_COMMA).join(',').trim())
    .filter(Boolean);
}

/**
 * Equipment grouped by facility for the Manufacturing Equipment accordion.
 *
 * Facilities with no equipment recorded are omitted, so the accordion's facility count reflects
 * sites that actually have equipment rather than every site on the profile.
 *
 * @param cdmo - CDMO profile
 * @returns Groups in facility display order; empty when nothing is recorded
 */
export function buildEquipmentGroups(cdmo: CDMOApplication): EquipmentGroup[] {
  return groupFacilitiesByRegion(cdmo)
    .map(({ facility, label }) => ({
      facilityLabel: label,
      items: parseEquipmentItems(facility.equipment),
    }))
    .filter((group) => group.items.length > 0);
}

/** Total equipment items across every facility — the first number in the accordion summary. */
export function countEquipmentItems(groups: EquipmentGroup[]): number {
  return groups.reduce((total, group) => total + group.items.length, 0);
}
